import { strings } from 'common';
import { Price } from 'models/reports/corporatePayload';
import React, { ReactNode } from 'react';
import { Card } from 'react-bootstrap';
import { currencyFormatter } from 'utils/formatter';
import { getFormattedDurationFromSeconds } from 'utils/time';
import './ReportRow.scss';
import { ratings } from './ReportsState';

const DEFAULT_MAX_TERMS = 2;
const INCLUDE_SECONDS = false;

interface ReportRowProps {
  columns: Array<{
    contents?: ReactNode;
    columnClass?: string;
    seconds?: number | null;
    secondsGoal?: number | null;
    money?: Price | null;
  }>;
  title: ReactNode;
  rowContentClassName?: string;
  rowTitleClassName?: string;
}

const formatDurationFromSeconds = (timeInSeconds: number): string =>
  getFormattedDurationFromSeconds(
    timeInSeconds,
    DEFAULT_MAX_TERMS,
    INCLUDE_SECONDS,
    {
      trim: true,
      trunc: true,
    }
  );

// returns '--' or '6d 21h'
export const determineOptionalTimeDisplay = (
  timeInSeconds: number | null
): string =>
  timeInSeconds
    ? formatDurationFromSeconds(timeInSeconds)
    : strings.EMPTY_VALUE;

const displayOptionalGoalTime = (
  timeInSeconds?: number | null,
  goalTimeInSeconds?: number | null
): string =>
  timeInSeconds && goalTimeInSeconds
    ? ' / ' + formatDurationFromSeconds(goalTimeInSeconds) + ' GOAL'
    : '';

// returns '--' or '73.05' or '+$90.65' or '-$89.48'
const displayOptionalDollarAmount = (
  price: Price | null,
  unshiftPlusSymbol = true
): string =>
  price && price?.amount
    ? `${unshiftPlusSymbol && price?.amount > 0 ? '+' : ''}${currencyFormatter(
        price?.amount
      )}`
    : strings.EMPTY_VALUE;

function selectContent(
  contents?: ReactNode,
  seconds?: number | null,
  money?: Price | null
) {
  if (contents !== undefined) return contents ?? strings.EMPTY_VALUE;
  if (seconds !== undefined) return determineOptionalTimeDisplay(seconds);
  if (money !== undefined) return displayOptionalDollarAmount(money);
  return strings.EMPTY_VALUE;
}
// return '' or 'bad' or 'good' style
const optionalDollarStyling = (price: Price | null) =>
  price && price.amount ? (price.amount > 0 ? ratings.BAD : ratings.GOOD) : '';
// return '' or 'bad' or 'good' style
const getOptionalExceededGoalStatusStyle = (
  timeValue: number | null,
  goalValue: number | null
) =>
  timeValue && goalValue
    ? timeValue > goalValue
      ? ratings.BAD
      : ratings.GOOD
    : '';
function computeCellClassName(
  time: { seconds?: number | null; secondsGoal?: number | null },
  money?: Price | null
) {
  if (money !== undefined) return optionalDollarStyling(money);
  if (time.seconds !== undefined && time.secondsGoal !== undefined) {
    return getOptionalExceededGoalStatusStyle(time.seconds, time.secondsGoal);
  }
  return '';
}
const ReportRow: React.FC<ReportRowProps> = ({
  title,
  columns,
  rowContentClassName,
  rowTitleClassName,
}) => (
  <Card>
    <div className="ReportRow flex-columns full-width border-bottom">
      <div
        className={
          rowTitleClassName ? rowTitleClassName : 'w-25 ReportRow-title'
        }
      >
        {title}
      </div>
      <div
        className={
          rowContentClassName
            ? rowContentClassName
            : 'w-75 evenly-spaced-container'
        }
      >
        {columns.map(
          ({ contents, columnClass, seconds, money, secondsGoal }) => (
            <div className={columnClass ? columnClass : 'evenly-spaced-item'}>
              <span
                className={`font-bold ${computeCellClassName(
                  { seconds, secondsGoal },
                  money
                )}`}
              >
                {selectContent(contents, seconds, money)}
              </span>
              <span className="CircularProgressChart-goal-time">
                {displayOptionalGoalTime(seconds, secondsGoal)}
              </span>
            </div>
          )
        )}
      </div>
    </div>
  </Card>
);

export default ReportRow;
